import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FiArrowUpRight, FiChevronDown } from 'react-icons/fi';
import { EXPERIENCE, dateRange, durationLabel, formatMonth, isCurrent } from '../../data/profile';
import { askChat } from '../ChatSection/chatEvents';
import SectionHeading from '../SectionHeading/SectionHeading';
import TechStack from '../ProjectSection/TechStack';
import './Experience.css';

const filters = [['tech', 'Software & IT'], ['all', 'All roles'], ['other', 'Other work']];
const ease = [0.16, 1, 0.3, 1];

const YEAR = /^(19|20)\d{2}$/;

// Figures carry the weight in a highlight ("saving 10+ hours a week"), so they get bolded
function Figures({ text }) {
  // Whole numbers only: "10+", "1,000+", "10–15", but not the 2 in "2FA" or a year
  return text
    .split(/(?<!\w)(\d[\d,]*(?:[–-]\d[\d,]*)?\+?)(?!\w)/)
    .map((part, i) => (i % 2 && !YEAR.test(part) ? <strong key={i}>{part}</strong> : part));
}

function Role({ job, index }) {
  const [open, setOpen] = useState(false);
  const current = isCurrent(job);
  const shown = job.highlights.slice(0, 2);
  const extra = job.highlights.slice(2);
  const panelId = `xp-more-${job.id}`;

  return (
    <motion.article layout="position" className={`xp-role ${current ? 'is-current' : ''}`} exit={{ opacity: 0, transition: { duration: 0.15 } }}>
      <div className="xp-role-inner" data-reveal style={{ '--i': index }}>
        <div className="xp-when">
          <span className="xp-dates tabular">{dateRange(job)}</span>
          <span className="xp-duration">{durationLabel(job.start, job.end)}</span>
          {current && <span className="xp-badge">Current role</span>}
        </div>
        <div className="xp-main">
          <h3>{job.role}</h3>
          <p className="xp-company">{job.company}<span> · {job.location}</span></p>
          {job.stages && (
            <ol className="xp-stages" aria-label="Progression">
              {job.stages.map((stage) => (
                <li key={stage.title}>
                  <strong>{stage.title}</strong>
                  <span className="tabular">{formatMonth(stage.start)} – {formatMonth(stage.end)}</span>
                </li>
              ))}
            </ol>
          )}
          <ul className="xp-highlights">{shown.map((h) => <li key={h}><Figures text={h} /></li>)}</ul>
          {job.stack && <TechStack stack={job.stack} className="xp-stack" />}

          <button type="button" className="xp-toggle" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((v) => !v)}>
            {open ? 'Less detail' : 'More detail'} <FiChevronDown aria-hidden="true" />
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                id={panelId}
                className="xp-more"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease }}
              >
                <div className="xp-more-inner">
                  {extra.length > 0 && <ul className="xp-highlights">{extra.map((h) => <li key={h}><Figures text={h} /></li>)}</ul>}
                  <p><strong>Skills:</strong> {job.skills.join(', ')}</p>
                  <p><strong>Workplace:</strong> {[job.employmentType, job.workplace].filter(Boolean).join(' · ')}</p>
                  <button type="button" className="xp-text-link" onClick={() => askChat(`Tell me about Diarmuid's role as ${job.role} at ${job.company}`)}>
                    Ask the chat assistant about this role
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.article>
  );
}

export default function Experience() {
  const [filter, setFilter] = useState('tech');
  const jobs = EXPERIENCE.filter((job) => filter === 'all' || job.type === filter);

  return (
    <section id="experience" className="section experience" aria-labelledby="experience-title">
      <div className="container">
        <SectionHeading
          title="Experience"
          id="experience-title"
          intro="Software engineering and IT first. Mentoring, election work and frontline jobs taught me the rest."
        />
        <div className="xp-filters" role="group" aria-label="Filter roles" data-reveal>
          {filters.map(([id, label]) => (
            <button key={id} type="button" aria-pressed={filter === id} className={`xp-filter ${filter === id ? 'is-active' : ''}`} onClick={() => setFilter(id)}>
              {filter === id && <motion.span layoutId="xp-filter-pill" className="xp-filter-pill" transition={{ type: 'spring', stiffness: 500, damping: 38 }} />}
              <span className="xp-filter-label">{label}</span>
            </button>
          ))}
        </div>
        <div className="xp-list">
          <AnimatePresence initial={false}>
            {jobs.map((job, i) => <Role key={job.id} job={job} index={i} />)}
          </AnimatePresence>
        </div>
        <div className="xp-end" data-reveal>
          <Link to="/cv" className="text-link">Full history in my CV <FiArrowUpRight className="icon-out" aria-hidden="true" /></Link>
          <button type="button" className="xp-text-link" onClick={() => askChat("Summarise Diarmuid's work experience")}>Ask the chat assistant</button>
        </div>
      </div>
    </section>
  );
}
